import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/auth.service';

// AIDEV-NOTE: Tipo local para AgendaVisita — migrar para @azoupvendas/shared quando estabilizado
export interface AgendaVisita {
  id: string;
  cliente_id: string;
  cliente_nome?: string;
  cliente_telefone?: string;
  cliente_endereco?: string;
  cliente_cidade?: string;
  data_planejada: string;
  tipo_visita: 'prospeccao' | 'manutencao' | 'cobranca' | 'entrega' | 'outro';
  observacoes?: string;
  status: 'planejada' | 'em_andamento' | 'concluida' | 'cancelada';
  visita_id?: string;
  // AIDEV-NOTE: Campos do objeto visitas aninhado retornado pelo backend via row_to_json
  visitas?: {
    checkin_at?: string;
    checkout_at?: string;
    duracao_minutos?: number;
  } | null;
  ordem_dia: number;
}

// AIDEV-NOTE: Usa instancia axios (api) em vez de fetch nativo para respeitar VITE_API_URL.
// fetch com URL relativa quebrava em producao (frontend e API em dominios diferentes).

// AIDEV-NOTE: Fuso IANA do dispositivo — enviado ao server para os filtros de dia da agenda
// respeitarem o dia local do usuário (server roda em UTC)
export function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
  } catch {
    return 'America/Sao_Paulo';
  }
}

// AIDEV-NOTE: Data local (YYYY-MM-DD) do dispositivo — toISOString() usava o dia UTC,
// que após as 21h locais (UTC-3) pedia a agenda do dia seguinte
export function localDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

async function fetchAgenda(data: string): Promise<AgendaVisita[]> {
  const res = await api.get<{ data: AgendaVisita[] }>(
    `/visitas/agenda?data=${data}&tz=${encodeURIComponent(deviceTimezone())}`
  );
  return res.data.data;
}

async function criarAgendaVisita(body: Record<string, unknown>): Promise<AgendaVisita> {
  const res = await api.post<AgendaVisita>('/visitas/agenda', body);
  return res.data;
}

async function reordenarAgenda(body: {
  data: string;
  ordem: { id: string; ordem_dia: number }[];
}): Promise<void> {
  await api.put('/visitas/agenda/reordenar', body);
}

export function useAgenda(data?: string) {
  const hoje = data ?? localDateString();
  return useQuery({
    queryKey: ['agenda', hoje],
    queryFn: () => fetchAgenda(hoje),
    staleTime: 2 * 60 * 1000
  });
}

export function useCriarAgendaVisita() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: criarAgendaVisita,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenda'] })
  });
}

export function useReordenarAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: reordenarAgenda,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenda'] })
  });
}

export function useCheckinAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      cliente_id: string;
      agenda_visita_id: string;
      lat: number;
      lng: number;
      accuracy?: number;
    }) => {
      const res = await api.post('/visitas/checkin', body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenda'] })
  });
}

export function useCheckoutAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      visita_id: string;
      lat: number;
      lng: number;
      accuracy?: number;
    }) => {
      const res = await api.post('/visitas/checkout', body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agenda'] })
  });
}
